<template>
  <div :id="computedId">
    <GenericFormModal
      :action="this.action"
      :canSubmit="this.canSubmit"
      :title="this.title"
      :isShown="this.isShown"
      :actionId="this.messageId"
      :id="computedId + '-modal'"
      :showButtons="!loading"
      @canceled="cancelOperation"
      @submited="onSubmitedData"
    >
      <Loading v-if="loading"></Loading>
      <div v-else>
        <InputText
          identifier="code"
          allowedChars="a-zA-Z \-\._"
          :maxLength="25"
          :label="'components.inputs.inputText.labelAlert' | i18n"
          icon="bell"
          :validations="computedValidationsCode"
          :informationTooltipContent="
            'components.inputs.labelTooltip.codeAlert' | i18n
          "
          @onChange="
            data => {
              code = data.value.trim();
              isCodeValid = data.validation.isValid;
            }
          "
          :initialValue="this.messageData.code"
          :forcedValidations="codeForcedValidations"
        />
        <InputText
          identifier="description"
          :label="'components.inputs.inputText.description' | i18n"
          :maxLength="255"
          icon="envelope"
          allowedChars="a-zA-Z \-\._"
          :validations="computedValidationsDescription"
          @onChange="
            data => {
              description = data.value.trim();
              isDescriptionValid = data.validation.isValid;
            }
          "
          :initialValue="this.messageData.description"
          :forcedValidations="descriptionForcedValidations"
        />
        <div class="d-inline-flex mr-5 w-100">
          <InputTextNumber
            identifier="seuil_avertissement"
            :informationTooltipContent="
              'components.inputs.labelTooltip.thresholdWarning' | i18n
            "
            :minimum="1"
            class="w-50 mr-5"
            :label="'components.inputs.inputNumber.labelWarning' | i18n"
            icon="exclamation triangle"
            @onChange="
              data => {
                warning = data.value;
                isWarningValid = data.validation.isValid;
              }
            "
            :maximum="999"
            :maxLength="3"
            :initialValue="this.messageData.seuil_avertissement"
            :forcedValidations="warningForcedValidations"
          />
          <InputTextNumber
            identifier="seuil_critique"
            :informationTooltipContent="
              'components.inputs.labelTooltip.thresholdCritic' | i18n
            "
            icon="exclamation circle"
            class="w-50"
            :label="'components.inputs.inputNumber.labelCritic' | i18n"
            :minimum="1"
            :maximum="999"
            :maxLength="3"
            @onChange="
              data => {
                critic = data.value;
                isCriticValid = data.validation.isValid;
              }
            "
            :initialValue="this.messageData.seuil_critique"
            :forcedValidations="criticForcedValidations"
          />
        </div>
        <input type="hidden" name="logiciel_id" :value="softwareId" />
      </div>
    </GenericFormModal>
    <b-toast
      :id="computedToastId"
      toast-class="bg-danger text-white text-center"
      :no-close-button="true"
    >
      <template v-slot:default>
        <i class="exclamation circle icon mr-3"></i>
        <span>{{ "sections.formServerSection.error" | i18n }}</span>
      </template>
    </b-toast>
  </div>
</template>
<script>
import GenericFormModal from "@/components/generics/GenericFormModal.vue";
import InputText from "@/components/inputs/InputText.vue";
import InputTextNumber from "@/components/inputs/InputTextNumber.vue";
import Loading from "@/components/others/Loading.vue";
/**
 * @description Formulaire pour ajouter un nouveau message d'erreur pour un logiciel
 * @author Francine Jin
 * @author Alexandre Gallant
 * @author Kevin Dubé
 * @vue-prop {Number} softwareId - Id du logiciel sur lequel le message sera ajouté
 * @vue-prop {Function} action - L'action à exécuter
 * @vue-prop {String} title - Le titre du formulaire
 * @vue-prop {Number} messageId - Id du message qu'on veux modifier si il y a lieu
 * @vue-prop {Boolean} isShown - Si le formulaire doit être affiché
 * @vue-prop {String} id - Id à donner au formulaire
 * @vue-data {Number} critic - Contient la valeur du seuil_critique
 * @vue-data {Number} warning - Contient la valeur du seuil_avertissement (seuil d'alerte)
 * @vue-data {Boolean} isCodeValid - Indique si le code du message est valide
 * @vue-data {Boolean} isDescriptionValid - Indique si la description du message est valide
 * @vue-data {Boolean} isWarningValid - Indique si seuil_avertissement est valide
 * @vue-data {Boolean} isCriticValid - Indique si seuil_critique est valide
 * @vue-data {Number} codeForcedValidations - Nombre de fois que les validations ont été forcées pour «code»
 * @vue-data {Number} descriptionForcedValidations - Nombre de fois que les validations ont été forcées pour «description»
 * @vue-data {Number} warningForcedValidations - Nombre de fois que les validations ont été forcées pour «warning»
 * @vue-data {Number} criticForcedValidations - Nombre de fois que les validations ont été forcées pour «critic»
 * @vue-data {Object} messageData - Contient les informations concernant le message à modifier
 * @vue-data {Boolean} loading - Définit si les informations du message sont encore en train d'être chargées
 * @vue-computed {Array} computedValidationCode - Retourne toutes les validations à effectuer sur le InputText "code"
 * @vue-computed {Array} computedValidationsDescription - Retourne toutes les validations à effectuer sur le InputText "description"
 * @vue-computed {String} computedToastId - L'id du toast
 * @vue-event {void} canceled - Est émis lorsque le formulaire est annulé
 * @vue-event {void} successed-request - Est émis lorsque le résultat de la requête est positif (200)
 */
export default {
  name: "FormThresholdSection",
  components: {
    InputTextNumber,
    InputText,
    GenericFormModal,
    Loading
  },
  /**
   * @description Effectue la requête nécessaire pour charger les informations du
   * message à modifier quand on ouvre le formulaire en mode édition
   */
  beforeMount() {
    if (this.messageId != undefined) {
      this.getMessage();
      this.isCodeValid = true;
      this.isDescriptionValid = true;
      this.isWarningValid = true;
      this.isCriticValid = true;
    }
  },
  props: {
    softwareId: {
      type: Number,
      required: true
    },
    action: {
      type: Function,
      required: true
    },
    title: {
      type: String,
      required: false,
      default: "Créer un message"
    },
    messageId: {
      type: Number,
      required: false
    },
    isShown: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      critic: 0,
      warning: 0,
      isCodeValid: false,
      isDescriptionValid: false,
      isWarningValid: false,
      isCriticValid: false,

      codeForcedValidations: 0,
      descriptionForcedValidations: 0,
      warningForcedValidations: 0,
      criticForcedValidations: 0,

      messageData: {},
      loading: false
    };
  },
  computed: {
    computedValidationsCode() {
      return [
        {
          error: this.$i18n.t("errors.empty", {
            name: this.$i18n.t("components.inputs.inputText.labelAlert")
          }),
          exec: v => v.trim() !== ""
        }
      ];
    },
    computedValidationsDescription() {
      return [
        {
          error: this.$i18n.t("errors.empty", {
            name: this.$i18n.t("components.inputs.inputText.description")
          }),
          exec: v => v.trim() !== ""
        }
      ];
    },
    computedId() {
      return "threshold-" + (this.messageId ? this.messageId : "new");
    },
    computedToastId() {
      return (
        "error-toast-threshold-" + (this.messageId ? this.messageId : "new")
      );
    }
  },
  methods: {
    /**
     * @description Vérifie la validité des données entrées
     * @returns {boolean} True si les données sont correctes
     */
    canSubmit() {
      const canSubmit =
        this.isCodeValid &&
        this.isDescriptionValid &&
        this.isWarningValid &&
        this.isCriticValid &&
        this.warning < this.critic;

      if (!canSubmit) {
        ++this.codeForcedValidations;
        ++this.descriptionForcedValidations;
        ++this.warningForcedValidations;
        ++this.criticForcedValidations;
      }

      return canSubmit;
    },
    /**
     * @description Émet que le formulaire a été annulé
     */
    cancelOperation() {
      this.$emit("canceled");
    },
    /**
     * @description Gère les opérations lorsque le formulaire a été envoyé
     */
    onSubmitedData(res) {
      res.status == 200
        ? this.$emit("successed-request")
        : this.$bvToast.show(this.computedToastId);
    },
    /**
     * @description Charge les données du message à modifier lorsqu'on ouvre
     * le formulaire avec l'action "edit"
     * @returns {Promise} Une promesse contenant les données
     * @throws {Error} Erreur lancée quand il n'y a pas de données
     */
    async getMessage() {
      this.loading = true;
      return await this.$api
        .getMessage(this.messageId)
        .then(res => {
          if (res.data && res.data.data) {
            this.loading = false;
            this.messageData = res.data.data;
            this.warning = +this.messageData.seuil_avertissement;
            this.critic = +this.messageData.seuil_critique;
          } else {
            throw new Error("No data received");
          }
        })
        .catch(() => {
          this.loading = false;
        });
    }
  }
};
</script>
